import {
  ManageContactsMutation,
  Plan,
  Relationship,
  UserAccountPrivileges,
  UserData,
  UserRole,
  Vehicles,
} from '../typings/main';

export const getPrimaryPhone = (userData: UserData) => {
  const { phones = [] } = userData;
  if (!phones || phones.length === 0) return 'Not phones data available.';

  const primaryPhone = phones.find((phone) => phone.primary);
  return primaryPhone ? primaryPhone.number : phones[0].number;
};

/**
 * Format phone number as (XXX) XXX-XXXX
 * @param value phone number string
 * @returns formatted number. and raw number
 */
export const formatPhoneNumber = (value: string) => {
  const cleaned: string = value.replace(/\D/g, '');
  const limitedInput: string = cleaned.substring(0, 10);

  let formatted: string = '';

  if (limitedInput.length > 0) {
    formatted = `(${limitedInput.substring(0, 3)}`;

    if (limitedInput.length > 3) {
      formatted += `) ${limitedInput.substring(3, 6)}`;
    }

    if (limitedInput.length > 6) {
      formatted += `-${limitedInput.substring(6, 10)}`;
    }
  }

  return { formatted, raw: limitedInput };
};

export const getFullName = (userData: UserData) => {
  const { firstName, middleName, lastName, suffix } = userData;
  let fullName = firstName;

  if (middleName) fullName += ` ${middleName}`;
  if (lastName) fullName += ` ${lastName}`;
  if (suffix) fullName += ` ${suffix}`;

  return fullName || 'Not provided';
};

export const getFormattedAddress = (userData: UserData) => {
  const { address } = userData;
  if (!address) return 'Not provided';

  const { address1, address2, city, state, zip, country } = address;
  let formattedAddress = '';

  if (address1) formattedAddress += address1;
  if (address2) formattedAddress += address2 ? `, ${address2}` : '';
  if (city || state) {
    formattedAddress += formattedAddress ? ', ' : '';
    formattedAddress += city ? `${city}` : '';
    formattedAddress += state ? `${city ? ', ' : ''}${state}` : '';
  }
  if (zip) formattedAddress += formattedAddress ? ` ${zip}` : zip;
  if (country) formattedAddress += formattedAddress ? `, ${country}` : country;

  return formattedAddress || 'Not provided';
};

export const findUserRole = (
  userAccountPrivileges: UserAccountPrivileges[],
): UserRole => {
  if (userAccountPrivileges.length === 0) return 'no-vehicle';
  const roles = userAccountPrivileges.flatMap((data) => data.roles);

  if (roles.includes('PU') && roles.includes('SU')) {
    return 'mixed';
  } else if (roles.includes('PU')) {
    return 'primary';
  } else if (roles.includes('SU')) {
    return 'additional';
  } else {
    return 'no-vehicle';
  }
};

export const isValidURL = (url: string | undefined | null): boolean => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};


export const checkImageLoads = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!isValidURL(url)) {
      resolve(false);
      return;
    }

    const img = new Image();

    img.onload = () => {
      resolve(true);
    };

    img.onerror = () => {
      resolve(false);
    };

    img.src = url;

    setTimeout(() => {
      if (!img.complete) {
        resolve(false);
      }
    }, 5000);
  });
};

export const filterActiveVehicles = (
  allVehicles: Vehicles[],
  userAccountPrivileges: UserAccountPrivileges[],
  country?: string,
  language?: string
) => {
  const isCanadaFrench = country?.toUpperCase() === 'CA' && language?.toLowerCase() === 'fr';

  const translateRole = (role: string): string => {
    switch (role) {
      case "PU":
        return isCanadaFrench ? "Utilisateur principal" : "Primary User";
      case "SU":
        return isCanadaFrench ? "Utilisateur supplémentaire" : "Additional User";
      default:
        return "";
    }
  };

  return allVehicles
    .filter((vehicle: Vehicles) => {
      const { relationships } = vehicle;

      return relationships.some(
        (relation: Relationship) =>
          relation.relationshipName === "CARNET_ENROLLMENT" &&
          relation.relationshipStatus === "AVAILABLE"
      );
    })
    .map((vehicle: Vehicles) => {
      const matchingPrivilege = userAccountPrivileges.find(
        (privilege) => privilege.vehicleId === vehicle.vehicleId
      );

      // If there's a match, add the translated role to the vehicle object
      if (matchingPrivilege && matchingPrivilege.roles && matchingPrivilege.roles.length > 0) {
        const translatedRole = translateRole(matchingPrivilege.roles[0]);

        return {
          ...vehicle,
          userRole: translatedRole
        };
      }

      return {
        ...vehicle,
        userRole: ""
      };
    });
};

const externalProviders = [
  'tmobile',
  't-mobile',
  'verizon',
  'siriusxm',
  'sirius-xm',
  'sirius',
  'tmo'
];

const isExternalSubscription = (plan: Plan): boolean => {
  const planName = plan.package?.name?.toLowerCase() || '';
  const providerName = plan.provider?.name?.toLowerCase() || '';

  return externalProviders.some(provider =>
    planName.includes(provider) || providerName.includes(provider)
  );
};

const isInternalSubscription = (plan: Plan): boolean => {
  return !isExternalSubscription(plan);
};

const hasValidStatus = (plan: Plan): boolean => {
  return plan.status !== null;
};

export const getUniqueExternalSubscriptions = (vehicles: Vehicles[]): Plan[] => {
  const allExternalSubscriptions = vehicles.flatMap((vehicle) => {
    const fromAllPlans = vehicle.subscriptions?.allPlans?.filter(
      plan => isExternalSubscription(plan) && hasValidStatus(plan)
    ) || [];

    const fromSubscribedPlans = vehicle.subscriptions?.subscribedPlans?.filter(
      plan => isExternalSubscription(plan) && hasValidStatus(plan)
    ) || [];

    return [...fromAllPlans, ...fromSubscribedPlans];
  });

  // Deduplicate subscriptions based on package SKU
  const uniqueSubscriptionsMap = new Map();

  allExternalSubscriptions.forEach((subscription: Plan) => {
    if (subscription.package?.sku) {
      uniqueSubscriptionsMap.set(subscription.package.sku, subscription);
    }
  });

  return Array.from(uniqueSubscriptionsMap.values());
};

export const getUniqueInternalSubscriptions = (vehicles: Vehicles[]): Plan[] => {
  const allInternalSubscriptions = vehicles.flatMap((vehicle) => {
    const fromAllPlans = vehicle.subscriptions?.allPlans?.filter(
      plan => isInternalSubscription(plan) && hasValidStatus(plan)
    ) || [];

    const fromSubscribedPlans = vehicle.subscriptions?.subscribedPlans?.filter(
      plan => isInternalSubscription(plan) && hasValidStatus(plan)
    ) || [];

    return [...fromAllPlans, ...fromSubscribedPlans];
  });

  const uniqueSubscriptionsMap = new Map();

  allInternalSubscriptions.forEach((subscription: Plan) => {
    if (subscription.package?.sku) {
      uniqueSubscriptionsMap.set(subscription.package.sku, subscription);
    }
  });

  return Array.from(uniqueSubscriptionsMap.values());
};

export const getPreferredVehicle = (vehicles: Vehicles[]) => {
  if (vehicles.length === 0) return undefined;

  return vehicles.find(vehicle => vehicle.preferred) || vehicles[0];
}

export const getUserPreferredVehiclePrivileges = (vehicleId: String | undefined, userVehiclesByPrivileges: UserAccountPrivileges[]) => {
  if (!vehicleId || userVehiclesByPrivileges.length === 0) return undefined;

  return userVehiclesByPrivileges.find(vehicle => vehicle.vehicleId == vehicleId) || null;
}

export const handleAdditionalContactsMutation = (contacts: string[], data: ManageContactsMutation) => {
  if(data.isEdit) {
    return contacts.map(contact => contact === data.initialValue ? data.value : contact);
  } else if(data.isRemove) {
    return contacts.filter(contact => contact !== data.value);
  } else {
    return [...contacts, data.value];
  }
}